import { keyBy, partition } from 'lodash'
import type { BaseDataItem, Component, PresetData } from '@wix/thunderbolt-becky-types'
import type { FeatureRefs } from '../cssFeatures.types'
import { createCssCompNode } from '../cssCompNode'
import { isRefArray, isVariantRelation } from '../../utils/variantsUtils'
import { isBreakpointRange, isPresetDataItem } from '../../utils/is'
import { BpToPresetsCss, ResolvedWidgetDataViewItem } from './presets.types'

const getPresetData = (presetVariant: BaseDataItem) => {
	if (isPresetDataItem(presetVariant)) {
		return {
			presetId: presetVariant.style,
			variants: [],
		}
	}
	if (isVariantRelation(presetVariant)) {
		return {
			presetId: (presetVariant.to as PresetData).style,
			variants: presetVariant.variants,
		}
	}
}

export const presets = createCssCompNode('presets', 'presets', {
	getDependencies: (component: Component, refs: FeatureRefs<'presets'>) =>
		component.componentType === 'BuilderComponent' && component.presetsQuery && component.dataQuery
			? {
					compPresets: refs.presetsQuery(component.presetsQuery),
					compData: refs.dataQuery(component.dataQuery),
			  }
			: null,

	toViewItem: (component, deps) => {
		if (!deps) {
			return null
		}

		const { compPresets, compData } = deps

		if (!compPresets || !compData) {
			return null
		}

		const { resolvedWidgetPresets } = compData as ResolvedWidgetDataViewItem
		if (!resolvedWidgetPresets) {
			return null
		}
		const presetsRefArray = isRefArray(compPresets) ? compPresets.values : [compPresets]

		const presetsAcc: BpToPresetsCss = {
			default: [
				{
					presetCss: resolvedWidgetPresets.default,
					variants: {},
				},
			],
		}

		return presetsRefArray.reduce((acc: BpToPresetsCss, preset) => {
			const { presetId, variants } = getPresetData(preset)!
			const [[breakpointVariant], variantsWithoutBp] = partition(variants, (variant) =>
				isBreakpointRange(variant)
			)

			const breakpointId = breakpointVariant?.id || 'default'
			acc[breakpointId] = acc[breakpointId] || []
			acc[breakpointId].push({
				presetCss: resolvedWidgetPresets[presetId],
				variants: keyBy(variantsWithoutBp, 'id'),
			})

			return acc
		}, presetsAcc)
	},
})
